import React, { useEffect, useState } from "react"
import { Box, Button, Divider, Snackbar, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { BiometricAuthenticationSettings } from "components/views/account/BiometricAuthenticationSettings"
import usePartnerAccess, { useUserRole } from "components/partner-access/hooks/usePartnerAccess"
import {
  PartnerInformationSettings,
  PartnerInformationSettingsPrompt
} from "components/views/account/PartnerInformationSettings"
import usePartnerInformation from "components/views/account/hooks/usePartnerInformation"
import { FeatureGate } from "utils/FeatureGate"
import { PersonalInformationSettings } from "components/views/account/PersonalInformationSettings"
import { PreferredLocale } from "components/views/account/PreferredLocale"
import { MasterLayout } from "components/views/atoms/layouts/MasterLayout"
import { useFlags } from "launchdarkly-react-client-sdk"
import { useFormContext } from "react-hook-form"
import { PartnerAccessAccountSettingsFooter } from "components/partner-access/member-account-settings/PartnerAccessAccountSettingsFooter"
import { AddressSettings } from "../../components/views/account/AddressSettings"
import { EmailCommunicationSettings } from "../../components/views/account/EmailCommunicationSettings"
import EmailAddressesAccountSettings from "../../components/EmailVerification/EmailAddressesAccountSettings"
import PasswordSettings from "components/views/account/PasswordSettings"
import { AccountDeletion } from "components/views/account/AccountDeletion"
import { FormattedMessage } from "react-intl"
import { useCurrentUser } from "components/context/user/UserContext"

export function SettingsPageDivider(): JSX.Element {
  return (
    <Box margin={(theme) => theme.spacing(theme.tokens.spacing.xxl, theme.tokens.spacing.none)}>
      <Divider />
    </Box>
  )
}

function SettingsPageHeader(): JSX.Element {
  const theme = useTheme()
  return (
    <Stack
      spacing={theme.spacing(theme.tokens.spacing.xs)}
      display="flex"
      flexDirection="column"
      justifyContent="flex-end"
      height="192px"
      width="100%"
    >
      <Stack direction="row" spacing={theme.spacing(theme.tokens.spacing.sm)} alignItems="center">
        <Box alt="" component="img" height={36} width={36} src="/images/icn-account-settings.png" />
        <Typography variant="h1">
          <FormattedMessage defaultMessage="Account settings" />
        </Typography>
      </Stack>
      <Divider />
    </Stack>
  )
}

export function SettingsPage({ isLoading }: { isLoading: boolean }): JSX.Element {
  const { showPreferredLocaleSelect } = useFlags()
  const { isPosingAsMember } = useCurrentUser()
  const {
    formState: { isDirty }
  } = useFormContext()
  const { hasProvidedAnyPartnerInfo } = usePartnerInformation()
  const [showPartnerInfo, setShowPartnerInfo] = useState(hasProvidedAnyPartnerInfo)
  const { isPartnerAccessingAccount } = useUserRole()
  const { showPartnerAccess } = usePartnerAccess()

  useEffect(() => {
    setShowPartnerInfo(hasProvidedAnyPartnerInfo)
  }, [hasProvidedAnyPartnerInfo])

  function handleShowPartnerInfoClick() {
    setShowPartnerInfo(true)
  }

  return (
    <MasterLayout header={<SettingsPageHeader />}>
      <Box maxWidth="432px">
        <PersonalInformationSettings />
        <SettingsPageDivider />
        <AddressSettings />
        <SettingsPageDivider />
        <BiometricAuthenticationSettings />
        <EmailAddressesAccountSettings />
        <SettingsPageDivider />
        <AccountDeletion />
        {!isPartnerAccessingAccount && (
          <>
            <PasswordSettings isPartnerSection={false} />
            <SettingsPageDivider />
          </>
        )}
        {showPreferredLocaleSelect && (
          <>
            <PreferredLocale />
            <SettingsPageDivider />
          </>
        )}
        <EmailCommunicationSettings />
        <FeatureGate filter={(c) => c.isPartnerEnabled}>
          {showPartnerInfo && (
            <>
              <SettingsPageDivider />
              <PartnerInformationSettings />
            </>
          )}
        </FeatureGate>
        <SettingsPageDivider />
        <Box marginBottom={(theme) => theme.spacing(theme.tokens.spacing.xxl)} display="flex" justifyContent="flex-end">
          <Button disabled={!isDirty || isLoading || isPosingAsMember} type="submit">
            <FormattedMessage defaultMessage="Save changes" />
          </Button>
        </Box>
        <Snackbar
          open={isPosingAsMember}
          message={
            "Editing account settings while posing as a member is not allowed. If you need to update member info, please do so in the Admin app."
          }
        />
      </Box>
      <FeatureGate filter={(c) => c.isPartnerEnabled}>
        {!showPartnerInfo && <PartnerInformationSettingsPrompt onClick={handleShowPartnerInfoClick} />}
      </FeatureGate>
      {showPartnerAccess && !isPartnerAccessingAccount && (
        <PartnerAccessAccountSettingsFooter addPartnerInfo={showPartnerInfo} />
      )}
    </MasterLayout>
  )
}
